import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { toast } from 'react-toastify';
import { convertApiDateToUtcDate } from 'helpers/utils';
import MessageService from '../../services/MessageService';
import { RootStore } from '../RootStore';
import { Message } from 'interfaces';

export class MessageStore {
  messages: Message.Message[] = [];
  lotData = {};
  lastReadMessage: string | number | null = null; // last message of another user the current user has read
  lastReadMessageAnotherUser: string | null = null; // last message of the current user that the other user has read
  conversations: Message.Conversation[] = [];
  currentConversation = 0;
  lastConversationMessage = null; // last message of the current conversation
  isLoading = false;
  unreadConversationsCount = 0;
  unreadConversationIndicator = false;
  unreadConversations: any = {};
  newConversation = {};
  messageService;
  selectedLotId: number;
  status: any;

  constructor(private rootStore: RootStore) {
    this.rootStore = rootStore;
    this.messageService = new MessageService();
    makeAutoObservable(this, {}, { autoBind: true });
  }

  sortConversations = () => {
    this.conversations = this.conversations
      .slice()
      .sort((current: any, next: any) => next.lastMessageAdded.getTime() - current.lastMessageAdded.getTime());
  };

  getNewConversation = () => this.newConversation;

  removeNewConversation = () => {
    sessionStorage.removeItem('newConversationData');
  };

  getSelectedLotId = () => this.selectedLotId;

  getConversations = async () => {
    try {
      const response = await this.messageService.getAllConversations();
      if (response.data && response.status === 200) {
        const conversationsWithDates = await Promise.all(
          response.data.pageContent.map(async (item: Message.Conversation) => {
            const lastMessageResponse = await this.messageService.getDetailLastMessage(item.id);
            const lastMessage = lastMessageResponse.data.pageContent[0];
            return {
              ...item,
              lastText: lastMessage.text,
              lastMessageAdded: convertApiDateToUtcDate(lastMessage.added),
              lastWroteUserId: lastMessage.userId,
              lastMessageId: lastMessage.id,
              theyLastReadMessageId: lastMessageResponse.data.theyLastReadMessageId,
              unreadMessagesCount: item.unreadMessagesCount,
            };
          })
        );

        runInAction(() => {
          this.conversations = conversationsWithDates;
          conversationsWithDates.forEach(conversation => {
            this.unreadConversations[conversation.id] = conversation.unreadMessagesCount;
          });
          this.sortConversations();
        });
      }
    } catch (error: any) {
      runInAction(() => {
        this.status = 'error';
      });
      if (error?.response && error.response.data) {
        toast.error(error.response.data);
      }
    }
  };

  getMessages = async (conversationId: number) => {
    try {
      if (conversationId !== -1) {
        const response = await this.messageService.getAllMessages(conversationId);

        if (response.data && response.status === 200) {
          runInAction(() => {
            this.selectedLotId = response.data.lotId;
            this.lastReadMessage = response.data.myLastReadMessageId;
            this.lastReadMessageAnotherUser = response.data.theyLastReadMessageId;
            this.messages = response.data.pageContent.reverse().map((m: Message.MessageResponse) => ({
              ...m,
              added: convertApiDateToUtcDate(m.added),
            })) as Message.Message[];
            this.unreadConversations[conversationId] = 0;
          });
        }
      }
    } catch (error: any) {
      runInAction(() => {
        this.status = 'error';
      });
      if (error.response && error.response.data) {
        toast.error(error.response.data);
      }
    }
  };

  setConversation = (conversationId: string) => {
    this.currentConversation = Number(conversationId);
  };

  sendMessage = async (messageModel: any, navigate: any) => {
    try {
      const response = await this.messageService.sendMessage(messageModel);
      if (response && response.status === 200) {
        if (messageModel.conversationId) {
          this.updateConversation(messageModel);
        } else {
          await this.getConversations();
        }
        runInAction(() => {
          toast.success('Message was successfully sent');
          if (this.currentConversation !== 0) {
            if (this.conversations.length > 0) {
              const latestConversation = this.conversations[0];
              if (latestConversation && navigate) {
                this.removeNewConversation();
                navigate(
                  `/conversation/${this.currentConversation !== -1 ? this.currentConversation : latestConversation.id}`
                );
              }
            }
          }
          this.getMessages(this.currentConversation);
        });
      }
    } catch (error: any) {
      runInAction(() => {
        this.status = 'error';
      });
      if (error.response && error.response.data) {
        toast.error(error.response.data);
      }
    }
  };

  updateConversation = async (message: Message.Message) => {
    const convers: Message.Conversation[] = [];
    const response = await this.messageService.getDetailLastMessage(message.conversationId);
    if (response.data && response.status === 200) {
      runInAction(() => {
        const mess = response.data.pageContent[0];
        this.conversations.forEach(value => {
          if (value.id === message.conversationId) {
            const lastMessage = { ...value };
            lastMessage.lastText = mess.text;
            lastMessage.lastMessageAdded = convertApiDateToUtcDate(mess.added);
            lastMessage.lastWroteUserId = mess.userId;
            lastMessage.lastMessageId = mess.id;
            lastMessage.theyLastReadMessageId = response.data.theyLastReadMessageId;
            convers.push(lastMessage);
          } else {
            convers.push(value);
          }
        });
      });
    }

    runInAction(() => {
      if (convers.length > 0) this.conversations = convers;
      this.sortConversations();
    });
  };

  updateLastReadMessage = async (id: number) => {
    try {
      const response = await this.messageService.setLastReadMessage(id, this.currentConversation);
      if (response.status === 200) {
        runInAction(() => {
          this.lastReadMessage = id;
          this.clearUnreadForConversation(this.currentConversation);
        });
      }
      await this.getUnreadConversationsCount();
    } catch {
      console.error("Can't mark lastReadMessage, something went wrong on the server");
    }
  };

  getUnreadConversationsCount = async () => {
    try {
      const countResponse = await this.messageService.getUnreadConversationsCount();
      if (countResponse && countResponse.status === 200) {
        runInAction(() => {
          this.unreadConversationsCount = countResponse.data;
        });
        runInAction(() => (this.unreadConversationIndicator = !!countResponse.data));
      }
    } catch (error: any) {
      runInAction(() => {
        this.status = 'error';
      });
      if (error.response && error.response.data) {
        toast.error(error.response.data);
      }
    }
  };

  getConversationByLotId = (lotId: number) => {
    let currentLotConversation = this.conversations.find(item => item.lotId === lotId);
    if (currentLotConversation) {
      currentLotConversation = toJS(currentLotConversation);
    }
    return currentLotConversation;
  };

  getConversationById = (id: number) => this.conversations.find(conversation => conversation.id === id);

  setUnreadConversation = (conversationId: number, messagesCount: number) => {
    this.unreadConversations = {
      ...this.unreadConversations,
      [conversationId]: messagesCount,
    };
  };

  clearUnreadForConversation = (conversationId: number) => {
    this.setUnreadConversation(conversationId, 0);
    this.conversations = this.conversations.map(conversation =>
      conversation.id === conversationId ? { ...conversation, unreadMessagesCount: 0 } : conversation
    );
  };

  updateCurrentConversation = (message: Message.MessageReceived) => {
    this.messages = [
      ...this.messages,
      {
        ...message,
        text: message.messageText,
        added: convertApiDateToUtcDate(message.added),
      },
    ];
  };

  handleNewMessage = async (message: Message.MessageReceived) => {
    const isCurrentChat = this.currentConversation === message.conversationId;

    if (isCurrentChat) {
      this.updateCurrentConversation(message);
    }

    if (this.conversations.length) {
      await this.getConversations();

      if (isCurrentChat) {
        runInAction(() => this.clearUnreadForConversation(message.conversationId));
      }
    }

    await this.getUnreadConversationsCount();

    if (isCurrentChat) {
      await this.updateLastReadMessage(message.id);
    }
  };

  clearConversationState = () => {
    this.messages = [];
    this.conversations = [];
  };
}
